// =============================================
// Parent Children Page
// Kipikid - מערכת נוכחות ילדים בגנים
// =============================================

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MapPin,
  Phone,
  Clock,
  CheckCircle2,
  Plus,
  ChevronLeft,
  Calendar,
  User,
  Building2,
  X,
  Camera,
  Image as ImageIcon,
  Bell,
  UserPlus,
  Send,
  AlertCircle,
  Users,
  Edit3,
  Trash2,
  Loader2,
  XCircle,
  AlertTriangle,
  CreditCard,
  Lock,
} from 'lucide-react';
import { API_BASE_URL, parentApi, childApi, kindergartenApi, attendanceApi, ChildRelation, UpdateChildRequest, KindergartenFromApi } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import ChildAvatar from '../../components/common/ChildAvatar';

// Types
interface Contact {
  id: number;
  name: string;
  phone: string;
  relation: string;
  isApproved: boolean;
  alertOrder: number | null; // 1 = first alert, 2 = second alert, null = no alerts
}

interface ChildData {
  id: number;
  publicId?: string; // UUID from server for API calls
  firstName: string;
  lastName: string;
  photoUrl?: string;
  // Note: Using kindergartenPublicId instead of internal Id for security
  kindergartenPublicId?: string;
  kindergartenName: string;
  kindergartenAddress: string;
  kindergartenPhone: string;
  arrivedAt?: string;
  expectedTime: string;
  birthDate?: string; // Format: YYYY-MM-DD
  isPayingParent?: boolean; // Whether current user is the paying parent for this child
  // Per-day arrival times from API
  sundayArrivalTime?: string | null;
  mondayArrivalTime?: string | null;
  tuesdayArrivalTime?: string | null;
  wednesdayArrivalTime?: string | null;
  thursdayArrivalTime?: string | null;
  fridayArrivalTime?: string | null;
}

interface KindergartenOperatingDay {
  dayOfWeek: number;       // 1=Sunday..6=Friday
  latestArrivalTime: string; // "HH:mm"
}

interface Kindergarten {
  // Note: Using publicId instead of internal Id for security
  publicId: string;
  name: string;
  city: string;
  street: string;
  houseNumber?: string;
  operatingDays?: KindergartenOperatingDay[];
}


// Calculate age from birthDate
const calculateAge = (birthDate?: string): string => {
  if (!birthDate) return '';
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age.toString();
};

// Default avatar for children without photos
const DEFAULT_CHILD_AVATAR = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80"><rect fill="#f3f4f6" width="80" height="80" rx="20"/><circle cx="40" cy="30" r="14" fill="#d1d5db"/><ellipse cx="40" cy="65" rx="22" ry="18" fill="#d1d5db"/></svg>')}`;

import { parentColors } from '../../styles/colors';

// Colors
const colors = {
  ...parentColors,
  gray: '#6b7280',
  grayLight: '#f3f4f6',
  background: '#f9fafb',
};


// Days of week for arrival times
const daysOfWeek = [
  { key: 'sunday', label: 'א', fullLabel: 'ראשון' },
  { key: 'monday', label: 'ב', fullLabel: 'שני' },
  { key: 'tuesday', label: 'ג', fullLabel: 'שלישי' },
  { key: 'wednesday', label: 'ד', fullLabel: 'רביעי' },
  { key: 'thursday', label: 'ה', fullLabel: 'חמישי' },
  { key: 'friday', label: 'ו', fullLabel: 'שישי' },
];

// Map day key to DayOfWeek enum (1=Sunday..6=Friday)
const dayKeyToDayOfWeek: Record<string, number> = {
  sunday: 1, monday: 2, tuesday: 3, wednesday: 4, thursday: 5, friday: 6,
};

// Get the latest arrival time for a specific day from kindergarten operating days
const getMaxArrivalTimeForDay = (
  kindergarten: Kindergarten | null | undefined,
  dayKey: string
): string | null => {
  if (!kindergarten?.operatingDays?.length) return null;
  const dow = dayKeyToDayOfWeek[dayKey];
  const opDay = kindergarten.operatingDays.find(d => d.dayOfWeek === dow);
  return opDay?.latestArrivalTime || null;
};

// Check if time1 > time2 (both in "HH:mm" format)
const isTimeLaterThan = (time1: string, time2: string): boolean => {
  const [h1, m1] = time1.split(':').map(Number);
  const [h2, m2] = time2.split(':').map(Number);
  return h1 * 60 + m1 > h2 * 60 + m2;
};

// Get the most restrictive (earliest) latest arrival time across selected days
const getEarliestMaxArrivalTime = (
  kindergarten: Kindergarten | null | undefined,
  arrivalTimes: Record<string, string | null>
): string | null => {
  if (!kindergarten?.operatingDays?.length) return null;
  let earliest: string | null = null;
  for (const [dayKey, time] of Object.entries(arrivalTimes)) {
    if (time === null) continue; // Day not active
    const maxTime = getMaxArrivalTimeForDay(kindergarten, dayKey);
    if (maxTime && (!earliest || isTimeLaterThan(earliest, maxTime))) {
      earliest = maxTime;
    }
  }
  return earliest;
};

// Validate arrival times against kindergarten operating days limits
// Returns error message or null if valid
const validateArrivalTimes = (
  kindergarten: Kindergarten | null | undefined,
  arrivalTimes: Record<string, string | null>
): string | null => {
  if (!kindergarten?.operatingDays?.length) return null;
  for (const day of daysOfWeek) {
    const time = arrivalTimes[day.key];
    if (!time) continue;
    const maxTime = getMaxArrivalTimeForDay(kindergarten, day.key);
    if (maxTime && isTimeLaterThan(time, maxTime)) {
      return `שעת ההגעה ליום ${day.fullLabel} (${time}) חורגת משעת ההגעה האחרונה של הגן (${maxTime})`;
    }
  }
  return null;
};

// Helper function to load children from localStorage
const loadChildrenFromStorage = (): ChildData[] => {
  try {
    const stored = localStorage.getItem('kipikid-children');
    if (stored) {
      const parsed = JSON.parse(stored);
      // Don't return mock data - only return real data
      if (parsed.length > 0 && parsed[0].publicId && !parsed[0].publicId.startsWith('mock-')) {
        return parsed;
      }
    }
  } catch (e) {
    console.error('Error loading children from storage:', e);
  }
  return []; // Return empty array instead of mock data
};

// Helper function to save children to localStorage
const saveChildrenToStorage = (children: ChildData[]) => {
  try {
    // Don't save SAS URLs to localStorage — they expire!
    // Save with cached base64 photos instead
    const toSave = children.map(c => ({
      ...c,
      // If photoUrl is a SAS URL (blob.core.windows.net), remove it — use cached base64 instead
      photoUrl: c.photoUrl?.startsWith('data:') ? c.photoUrl : undefined,
    }));
    localStorage.setItem('kipikid-children', JSON.stringify(toSave));
  } catch (e) {
    console.error('Error saving children to storage:', e);
  }
};

// ===============================================
// Parent Photo Cache Helpers
// Uses same pattern as tablet — stores base64 in localStorage
// ===============================================
const PARENT_PHOTO_PREFIX = 'kipikid-parent-photo-';

function saveParentPhoto(childPublicId: string, base64: string): void {
  try {
    localStorage.setItem(`${PARENT_PHOTO_PREFIX}${childPublicId}`, base64);
  } catch (e) {
    console.error('[ParentPhotoCache] Failed to save photo:', e);
    // If localStorage full, clean up oldest photos
    cleanupParentPhotos();
  }
}

function getParentPhoto(childPublicId: string): string | null {
  return localStorage.getItem(`${PARENT_PHOTO_PREFIX}${childPublicId}`);
}

function cleanupParentPhotos(): void {
  console.warn('[ParentPhotoCache] Cleaning up photos due to storage limit...');
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(PARENT_PHOTO_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  // Remove oldest 30% of photos
  const toRemove = Math.ceil(keysToRemove.length * 0.3);
  for (let i = 0; i < toRemove; i++) {
    localStorage.removeItem(keysToRemove[i]);
  }
}

/** Download photo from SAS URL and cache as base64 in localStorage */
async function downloadAndCachePhoto(childPublicId: string, sasUrl: string): Promise<string | null> {
  try {
    const response = await fetch(sasUrl);
    if (!response.ok) return null;
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        saveParentPhoto(childPublicId, base64);
        resolve(base64);
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.error('[ParentPhotoCache] Failed to download photo:', e);
    return null;
  }
}

/** Restore cached base64 photos to children loaded from localStorage */
function restorePhotosFromCache(children: ChildData[]): ChildData[] {
  return children.map(child => {
    if (child.photoUrl) return child; // Already has photo (data: URI)
    if (!child.publicId) return child;
    const cached = getParentPhoto(child.publicId);
    if (cached) {
      return { ...child, photoUrl: cached };
    }
    return child;
  });
}

/** Cache all children's photos — downloads from SAS URL only if not already cached */
async function cacheChildrenPhotos(children: ChildRelation[]): Promise<Record<string, string>> {
  const photoMap: Record<string, string> = {};

  const downloadPromises = children.map(async (child) => {
    if (!child.childPhotoUrl) return;

    // Check if we already have a cached version
    const cached = getParentPhoto(child.childPublicId);
    if (cached) {
      photoMap[child.childPublicId] = cached;
      return;
    }

    // Download and cache the photo
    const base64 = await downloadAndCachePhoto(child.childPublicId, child.childPhotoUrl);
    if (base64) {
      photoMap[child.childPublicId] = base64;
    }
  });

  await Promise.all(downloadPromises);
  return photoMap;
}

// Convert API ChildRelation to local ChildData format
/** Get child's arrival time for today (by day of week) */
const getTodayArrivalTime = (
  sun?: string | null, mon?: string | null, tue?: string | null,
  wed?: string | null, thu?: string | null, fri?: string | null
): string | null => {
  const day = new Date().getDay(); // 0=Sun,1=Mon,...
  switch (day) {
    case 0: return sun || null;
    case 1: return mon || null;
    case 2: return tue || null;
    case 3: return wed || null;
    case 4: return thu || null;
    case 5: return fri || null;
    default: return null;
  }
};

const convertChildRelationToChildData = (relation: ChildRelation, index: number): ChildData => ({
  id: index + 1,
  publicId: relation.childPublicId,
  firstName: relation.childFirstName,
  lastName: relation.childLastName,
  photoUrl: relation.childPhotoUrl,
  kindergartenPublicId: relation.kindergartenPublicId,
  kindergartenName: relation.kindergartenName,
  kindergartenAddress: '', // Not available from API
  kindergartenPhone: '', // Not available from API
  arrivedAt: undefined,
  // Use arrival time for TODAY (based on day of week), fallback to 08:30
  expectedTime: getTodayArrivalTime(
    relation.sundayArrivalTime, relation.mondayArrivalTime, relation.tuesdayArrivalTime,
    relation.wednesdayArrivalTime, relation.thursdayArrivalTime, relation.fridayArrivalTime
  ) || relation.sundayArrivalTime || '08:30',
  birthDate: undefined, // Will come from API when available
  isPayingParent: relation.isPayingParent, // Whether current user is paying parent
  // Preserve per-day arrival times
  sundayArrivalTime: relation.sundayArrivalTime || null,
  mondayArrivalTime: relation.mondayArrivalTime || null,
  tuesdayArrivalTime: relation.tuesdayArrivalTime || null,
  wednesdayArrivalTime: relation.wednesdayArrivalTime || null,
  thursdayArrivalTime: relation.thursdayArrivalTime || null,
  fridayArrivalTime: relation.fridayArrivalTime || null,
});

export default function ParentChildren() {
  const navigate = useNavigate();
  const [children, setChildren] = useState<ChildData[]>([]);
  const [selectedChild, setSelectedChild] = useState<ChildData | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingChild, setEditingChild] = useState<ChildData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [kindergartens, setKindergartens] = useState<Kindergarten[]>([]);
  const [isPayingParent, setIsPayingParent] = useState<boolean>(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Message modal state
  const [messageModal, setMessageModal] = useState<{
    show: boolean;
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
  } | null>(null);

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState<{
    show: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText?: string;
    cancelText?: string;
  } | null>(null);

  const showMessage = (type: 'success' | 'error' | 'warning' | 'info', title: string, message: string) => {
    setMessageModal({ show: true, type, title, message });
  };

  const closeMessage = () => {
    setMessageModal(null);
  };

  const showConfirm = (title: string, message: string, onConfirm: () => void, confirmText = 'אישור', cancelText = 'ביטול') => {
    setConfirmModal({ show: true, title, message, onConfirm, confirmText, cancelText });
  };

  const closeConfirm = () => {
    setConfirmModal(null);
  };

  // Load kindergartens from API
  useEffect(() => {
    const loadKindergartens = async () => {
      try {
        const response = await kindergartenApi.getAll();
        if (response.success && response.data && response.data.length > 0) {
          const apiKindergartens: Kindergarten[] = response.data.map(k => ({
            publicId: k.publicId,
            name: k.name,
            city: k.city,
            street: k.street,
            houseNumber: k.houseNumber,
            operatingDays: k.operatingDays?.map(d => ({
              dayOfWeek: d.dayOfWeek,
              latestArrivalTime: d.latestArrivalTime,
            })),
          }));
          setKindergartens(apiKindergartens);
          console.log('[ParentChildren] Loaded kindergartens from API:', apiKindergartens.length);
        } else {
          console.log('[ParentChildren] Using mock kindergartens');
        }
      } catch (err) {
        console.error('Error loading kindergartens:', err);
        // Keep using mock kindergartens
      }
    };
    loadKindergartens();
  }, []);

  // Load children from API on mount
  useEffect(() => {
    const loadChildren = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await parentApi.getMyChildren();

        if (response.success && response.data && response.data.length > 0) {
          // Check if user is a paying parent for any child
          const hasPayingParentRelation = response.data.some(child => child.isPayingParent);
          setIsPayingParent(hasPayingParentRelation);

          // Download and cache photos in localStorage (runs in background)
          // Use cached base64 photos instead of SAS URLs (which expire)
          const photoMap = await cacheChildrenPhotos(response.data);

          const apiChildren = response.data.map((relation, index) => {
            const child = convertChildRelationToChildData(relation, index);
            // Use cached base64 photo instead of SAS URL
            const cachedPhoto = photoMap[relation.childPublicId] || getParentPhoto(relation.childPublicId);
            if (cachedPhoto) {
              child.photoUrl = cachedPhoto;
            }
            return child;
          });

          // Merge with local modifications from localStorage
          const storedChildren = loadChildrenFromStorage();
          const mergedChildren = apiChildren.map(apiChild => {
            const storedChild = storedChildren.find(s => s.publicId === apiChild.publicId);
            if (storedChild) {
              // Keep ALL local modifications - they override API data
              // But prefer fresh cached photo over stored (which has no photo since we strip SAS URLs)
              return {
                ...apiChild,
                ...storedChild,
                // Keep API's basic identity
                id: apiChild.id,
                publicId: apiChild.publicId,
                firstName: storedChild.firstName || apiChild.firstName,
                lastName: storedChild.lastName || apiChild.lastName,
                // Always prefer cached base64 photo
                photoUrl: apiChild.photoUrl || storedChild.photoUrl,
              };
            }
            return apiChild;
          });

          // Also include locally-added children (not from API)
          const localOnlyChildren = storedChildren.filter(stored =>
            !apiChildren.some(api => api.publicId === stored.publicId)
          );

          const allChildren = [...mergedChildren, ...localOnlyChildren];
          setChildren(allChildren);
          saveChildrenToStorage(allChildren);
        } else if (response.success && response.data?.length === 0) {
          // No children from API - check localStorage
          const storedChildren = restorePhotosFromCache(loadChildrenFromStorage());
          if (storedChildren.length > 0) {
            setChildren(storedChildren);
          } else {
            setChildren([]);
            setError('אין ילדים רשומים במערכת');
          }
        } else {
          // API error - fallback to localStorage (with cached photos)
          const storedChildren = restorePhotosFromCache(loadChildrenFromStorage());
          if (storedChildren.length > 0) {
            setChildren(storedChildren);
          } else {
            setError(response.message || 'שגיאה בטעינת ילדים');
          }
        }
      } catch (err) {
        console.error('Error loading children:', err);
        // Fallback to localStorage with cached photos
        const storedChildren = restorePhotosFromCache(loadChildrenFromStorage());
        if (storedChildren.length > 0) {
          setChildren(storedChildren);
        } else {
          setError('שגיאה בטעינת ילדים מהשרת');
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadChildren();
  }, []);

  // Track attendance status separately to avoid infinite loops
  // 'arrived:HH:mm' = arrived with time, 'not_arrived' = confirmed not arrived, undefined = unknown
  const [attendanceMap, setAttendanceMap] = useState<Record<string, string | undefined>>({});

  // Fetch attendance status for all children (and refresh every 30s)
  useEffect(() => {
    if (children.length === 0) return;

    const childPublicIds = children.filter(c => c.publicId).map(c => c.publicId!);
    if (childPublicIds.length === 0) return;

    const fetchAttendance = async () => {
      try {
        const newMap: Record<string, string | undefined> = {};

        // Batch endpoint: single API call for all children
        const result = await attendanceApi.getChildrenStatusBatch(childPublicIds).catch((err) => {
          console.warn(`[ParentChildren] batch status failed, falling back:`, err?.message || err);
          return null;
        });

        if (result?.success && result.data) {
          for (const record of result.data) {
            if (record.arrivedAt && record.childPublicId) {
              const match = record.arrivedAt.match(/T(\d{2}:\d{2})/);
              const timeStr = match ? match[1] : record.arrivedAt.substring(0, 5);
              if (timeStr !== '00:00') { // Skip MinValue placeholder
                newMap[record.childPublicId] = timeStr;
              }
            }
          }
        } else {
          // Fallback to individual calls
          const statuses = await Promise.all(
            childPublicIds.map(id =>
              attendanceApi.getChildStatus(id).catch(() => null)
            )
          );
          childPublicIds.forEach((id, i) => {
            const res = statuses[i];
            if (res?.success && res.data?.arrivedAt) {
              const match = res.data.arrivedAt.match(/T(\d{2}:\d{2})/);
              newMap[id] = match ? match[1] : res.data.arrivedAt.substring(0, 5);
            }
          });
        }

        setAttendanceMap(prev => {
          const changed = childPublicIds.some(id => prev[id] !== newMap[id]);
          return changed ? newMap : prev;
        });
      } catch (err) {
        console.error('[ParentChildren] Error fetching attendance:', err);
      }
    };

    fetchAttendance();
    const interval = setInterval(fetchAttendance, 60_000); // Every 60 seconds (was 30s)
    return () => clearInterval(interval);
  }, [children.length]); // Re-run when children list changes size

  // Show success message helper
  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  // Add child handler - saves to API
  const handleAddChild = async (newChild: ChildData) => {
    setIsSaving(true);
    setError(null);
    try {
      // Prepare API request
      const request = {
        firstName: newChild.firstName,
        lastName: newChild.lastName,
        birthDate: newChild.birthDate,
        kindergartenPublicId: newChild.kindergartenPublicId || '', // From kindergarten search
        photoBase64: newChild.photoUrl?.startsWith('data:') ? newChild.photoUrl : undefined,
        photoContentType: newChild.photoUrl?.startsWith('data:image/png') ? 'image/png' : 'image/jpeg',
        sundayArrivalTime: newChild.expectedTime,
        mondayArrivalTime: newChild.expectedTime,
        tuesdayArrivalTime: newChild.expectedTime,
        wednesdayArrivalTime: newChild.expectedTime,
        thursdayArrivalTime: newChild.expectedTime,
        fridayArrivalTime: undefined, // No arrival on Friday by default
      };

      const response = await childApi.createChild(request);

      if (response.success && response.data) {
        // Cache the photo if available
        let cachedPhotoUrl: string | undefined = undefined;
        if (request.photoBase64 && response.data.publicId) {
          // Photo was uploaded — cache the base64 directly
          saveParentPhoto(response.data.publicId, request.photoBase64);
          cachedPhotoUrl = request.photoBase64;
        } else if (response.data.photoUrl && response.data.photoUrl.includes('blob.core.windows.net') && response.data.publicId) {
          // Download from SAS and cache
          const cached = await downloadAndCachePhoto(response.data.publicId, response.data.photoUrl);
          if (cached) cachedPhotoUrl = cached;
        }

        // Update local state with new child from API
        const createdChild: ChildData = {
          id: children.length + 1,
          publicId: response.data.publicId,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          photoUrl: cachedPhotoUrl || response.data.photoUrl,
          kindergartenName: response.data.kindergartenName,
          kindergartenAddress: '',
          kindergartenPhone: '',
          expectedTime: getTodayArrivalTime(
            response.data.sundayArrivalTime, response.data.mondayArrivalTime, response.data.tuesdayArrivalTime,
            response.data.wednesdayArrivalTime, response.data.thursdayArrivalTime, response.data.fridayArrivalTime
          ) || response.data.sundayArrivalTime || '08:30',
          birthDate: response.data.birthDate,
        };

        const updatedChildren = [...children, createdChild];
        setChildren(updatedChildren);
        saveChildrenToStorage(updatedChildren);
        setShowAddModal(false);
        showSuccess(response.message || `הילד ${newChild.firstName} נשמר בהצלחה`);
      } else {
        setError(response.message || 'שגיאה בשמירת הילד');
      }
    } catch (err) {
      console.error('Error creating child:', err);
      setError('שגיאה בשמירת הילד לשרת');
    } finally {
      setIsSaving(false);
    }
  };

  // Update child handler - saves to API
  const handleUpdateChild = async (updatedChild: ChildData) => {
    if (!updatedChild.publicId) {
      // No publicId - just update locally
      const updatedChildren = children.map(c =>
        c.id === updatedChild.id ? updatedChild : c
      );
      setChildren(updatedChildren);
      saveChildrenToStorage(updatedChildren);
      setEditingChild(null);
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      // Build update request - use per-day arrival times
      // null means "clear this day" -> send empty string to backend
      // string means "set this time" -> send the time
      // undefined means "not set" -> fallback to expectedTime
      const mapTime = (t: string | null | undefined, fallback: string) => {
        if (t === null) return ''; // Clear this day on backend
        return t || fallback;
      };
      const request: UpdateChildRequest = {
        firstName: updatedChild.firstName,
        lastName: updatedChild.lastName,
        birthDate: updatedChild.birthDate,
        kindergartenPublicId: updatedChild.kindergartenPublicId,
        sundayArrivalTime: mapTime(updatedChild.sundayArrivalTime, updatedChild.expectedTime),
        mondayArrivalTime: mapTime(updatedChild.mondayArrivalTime, updatedChild.expectedTime),
        tuesdayArrivalTime: mapTime(updatedChild.tuesdayArrivalTime, updatedChild.expectedTime),
        wednesdayArrivalTime: mapTime(updatedChild.wednesdayArrivalTime, updatedChild.expectedTime),
        thursdayArrivalTime: mapTime(updatedChild.thursdayArrivalTime, updatedChild.expectedTime),
        fridayArrivalTime: mapTime(updatedChild.fridayArrivalTime, ''),
      };

      // Handle photo - check if it's a new base64 image
      if (updatedChild.photoUrl?.startsWith('data:')) {
        request.photoBase64 = updatedChild.photoUrl;
        request.photoContentType = updatedChild.photoUrl.startsWith('data:image/png') ? 'image/png' : 'image/jpeg';
      }

      console.log('[handleSaveChild] Updating child:', {
        publicId: updatedChild.publicId,
        kindergartenPublicId: request.kindergartenPublicId,
        firstName: request.firstName,
        arrivalTimes: {
          sun: request.sundayArrivalTime,
          mon: request.mondayArrivalTime,
          tue: request.tuesdayArrivalTime,
          wed: request.wednesdayArrivalTime,
          thu: request.thursdayArrivalTime,
          fri: request.fridayArrivalTime,
        },
        hasPhoto: !!request.photoBase64
      });

      const response = await childApi.updateChild(updatedChild.publicId, request);

      if (response.success && response.data) {
        // If photo was updated, download new photo and cache it
        let finalPhotoUrl = updatedChild.photoUrl;
        if (request.photoBase64 && updatedChild.publicId) {
          // Photo was just uploaded as base64 — cache it directly
          saveParentPhoto(updatedChild.publicId, request.photoBase64);
          finalPhotoUrl = request.photoBase64;
        } else if (response.data.photoUrl && response.data.photoUrl.includes('blob.core.windows.net') && updatedChild.publicId) {
          // New SAS URL from API — download and cache
          const cached = await downloadAndCachePhoto(updatedChild.publicId, response.data.photoUrl);
          if (cached) finalPhotoUrl = cached;
        }

        const updated: ChildData = {
          ...updatedChild,
          photoUrl: finalPhotoUrl,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          kindergartenPublicId: response.data.kindergartenPublicId,
          kindergartenName: response.data.kindergartenName,
          // Update per-day arrival times from API response
          sundayArrivalTime: response.data.sundayArrivalTime || null,
          mondayArrivalTime: response.data.mondayArrivalTime || null,
          tuesdayArrivalTime: response.data.tuesdayArrivalTime || null,
          wednesdayArrivalTime: response.data.wednesdayArrivalTime || null,
          thursdayArrivalTime: response.data.thursdayArrivalTime || null,
          fridayArrivalTime: response.data.fridayArrivalTime || null,
          expectedTime: getTodayArrivalTime(
            response.data.sundayArrivalTime, response.data.mondayArrivalTime, response.data.tuesdayArrivalTime,
            response.data.wednesdayArrivalTime, response.data.thursdayArrivalTime, response.data.fridayArrivalTime
          ) || response.data.sundayArrivalTime || updatedChild.expectedTime,
        };

        const updatedChildren = children.map(c =>
          c.id === updatedChild.id ? updated : c
        );
        setChildren(updatedChildren);
        saveChildrenToStorage(updatedChildren);
        setEditingChild(null);
        showSuccess(response.message || `השינויים של ${updatedChild.firstName} נקלטו בהצלחה`);
      } else {
        setError(response.message || 'שגיאה בעדכון הילד');
      }
    } catch (err) {
      console.error('Error updating child:', err);
      setError('שגיאה בעדכון הילד בשרת');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete child handler - deletes from API
  const handleDeleteChild = async (childId: number) => {
    const childToDelete = children.find(c => c.id === childId);
    if (!childToDelete) return;

    showConfirm(
      'מחיקת ילד/ה',
      'האם אתה בטוח שברצונך למחוק את הילד/ה?',
      async () => {
        closeConfirm();

        if (!childToDelete.publicId) {
          // No publicId - just delete locally
          const updatedChildren = children.filter(c => c.id !== childId);
          setChildren(updatedChildren);
          saveChildrenToStorage(updatedChildren);
          setEditingChild(null);
          return;
        }

        setIsSaving(true);
        setError(null);
        try {
          const response = await childApi.deleteChild(childToDelete.publicId);

          if (response.success) {
            const updatedChildren = children.filter(c => c.id !== childId);
            setChildren(updatedChildren);
            saveChildrenToStorage(updatedChildren);
            setEditingChild(null);
            showSuccess(response.message || `הילד ${childToDelete.firstName} נמחק בהצלחה`);
          } else {
            setError(response.message || 'שגיאה במחיקת הילד');
          }
        } catch (err) {
          console.error('Error deleting child:', err);
          setError('שגיאה במחיקת הילד מהשרת');
        } finally {
          setIsSaving(false);
        }
      },
      'מחק',
      'ביטול'
    );
  };

  // Show loading state
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        gap: '16px',
      }}>
        <Loader2 size={40} style={{ color: colors.primary, animation: 'spin 1s linear infinite' }} />
        <p style={{ color: colors.textLight }}>טוען ילדים...</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Error message */}
      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fee2e2',
          color: '#dc2626',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {/* Success message */}
      {successMessage && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          style={{
            padding: '12px 16px',
            backgroundColor: '#dcfce7',
            color: '#16a34a',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <CheckCircle2 size={18} />
          {successMessage}
        </motion.div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: colors.text }}>
          הילדים שלי
        </h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          {/* Only show contacts button if user is paying parent for at least one child */}
          {children.some(c => c.isPayingParent) && (
            <motion.button
              onClick={() => navigate('/parent/contacts')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                backgroundColor: colors.primaryLight,
                color: colors.primary,
                border: 'none',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Users size={18} />
              אנשי קשר
            </motion.button>
          )}
          <motion.button
            onClick={() => {
              if (isPayingParent) {
                setShowAddModal(true);
              } else {
                setShowPaymentModal(true);
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              backgroundColor: colors.secondary,
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Plus size={18} />
            הוסף ילד
          </motion.button>
        </div>
      </div>

      {/* Children Grid */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {children.map((child, index) => {
          // Merge attendance status from real-time polling
          const childWithAttendance = child.publicId && attendanceMap[child.publicId]
            ? { ...child, arrivedAt: attendanceMap[child.publicId] }
            : child;
          return (
            <ChildCard
              key={child.id}
              child={childWithAttendance}
              index={index}
              onClick={() => setSelectedChild(childWithAttendance)}
              onEdit={() => child.isPayingParent ? setEditingChild(child) : null}
            />
          );
        })}
      </div>

      {/* Child Details Modal */}
      <AnimatePresence>
        {selectedChild && (
          <ChildDetailsModal
            child={selectedChild}
            onClose={() => setSelectedChild(null)}
          />
        )}
      </AnimatePresence>

      {/* Add Child Modal */}
      <AnimatePresence>
        {showAddModal && (
          <AddChildModal
            kindergartens={kindergartens}
            existingContacts={[]}
            onClose={() => setShowAddModal(false)}
            onAdd={handleAddChild}
            onShowMessage={showMessage}
          />
        )}
      </AnimatePresence>

      {/* Edit Child Modal */}
      <AnimatePresence>
        {editingChild && (
          <EditChildModal
            child={editingChild}
            kindergartens={kindergartens}
            onClose={() => setEditingChild(null)}
            onUpdate={handleUpdateChild}
            onDelete={handleDeleteChild}
            onShowMessage={showMessage}
          />
        )}
      </AnimatePresence>

      {/* Message Modal */}
      <AnimatePresence>
        {messageModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) {
                closeMessage();
              }
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
              padding: '20px',
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={{
                backgroundColor: 'white',
                borderRadius: '20px',
                padding: '24px',
                width: '100%',
                maxWidth: '320px',
                textAlign: 'center',
              }}
            >
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                backgroundColor: messageModal.type === 'success' ? colors.successLight :
                                messageModal.type === 'error' ? colors.dangerLight :
                                messageModal.type === 'warning' ? colors.warningLight :
                                colors.primaryLight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                {messageModal.type === 'success' && <CheckCircle2 size={28} color={colors.success} />}
                {messageModal.type === 'error' && <XCircle size={28} color={colors.danger} />}
                {messageModal.type === 'warning' && <AlertTriangle size={28} color={colors.warning} />}
                {messageModal.type === 'info' && <Bell size={28} color={colors.primary} />}
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text, margin: '0 0 8px 0' }}>
                {messageModal.title}
              </h3>
              <p style={{ fontSize: '14px', color: colors.textLight, margin: '0 0 20px 0', lineHeight: '1.5' }}>
                {messageModal.message}
              </p>
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={closeMessage}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '12px',
                  backgroundColor: messageModal.type === 'success' ? colors.success :
                                  messageModal.type === 'error' ? colors.danger :
                                  messageModal.type === 'warning' ? colors.warning :
                                  colors.primary,
                  color: 'white',
                  fontSize: '15px',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                אישור
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirm Modal */}
      <AnimatePresence>
        {confirmModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) {
                closeConfirm();
              }
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
              padding: '20px',
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={{
                backgroundColor: 'white',
                borderRadius: '20px',
                padding: '24px',
                width: '100%',
                maxWidth: '320px',
                textAlign: 'center',
              }}
            >
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                backgroundColor: colors.warningLight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <AlertTriangle size={28} color={colors.warning} />
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text, margin: '0 0 8px 0' }}>
                {confirmModal.title}
              </h3>
              <p style={{ fontSize: '14px', color: colors.textLight, margin: '0 0 20px 0', lineHeight: '1.5' }}>
                {confirmModal.message}
              </p>
              <div style={{ display: 'flex', gap: '12px' }}>
                <motion.button
                  autoFocus
                  whileTap={{ scale: 0.98 }}
                  onClick={closeConfirm}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '12px',
                    backgroundColor: colors.background,
                    color: colors.text,
                    fontSize: '15px',
                    fontWeight: '600',
                    border: `1px solid ${colors.border}`,
                    cursor: 'pointer',
                  }}
                >
                  {confirmModal.cancelText}
                </motion.button>
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={confirmModal.onConfirm}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '12px',
                    backgroundColor: colors.danger,
                    color: 'white',
                    fontSize: '15px',
                    fontWeight: '600',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {confirmModal.confirmText}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Payment Required Modal */}
      <AnimatePresence>
        {showPaymentModal && (
          <PaymentModal
            onClose={() => setShowPaymentModal(false)}
            onPaymentSuccess={() => {
              setIsPayingParent(true);
              setShowPaymentModal(false);
              setShowAddModal(true);
              showMessage('success', 'תשלום התקבל', 'כעת תוכל להוסיף ילדים למערכת');
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Payment Modal Component - Mock Tranzila-style payment screen
function PaymentModal({ onClose, onPaymentSuccess }: { onClose: () => void; onPaymentSuccess: () => void }) {
  const [step, setStep] = useState<'info' | 'payment' | 'processing' | 'success'>('info');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 16);
    const groups = digits.match(/.{1,4}/g) || [];
    return groups.join(' ');
  };

  const formatExpiry = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    if (digits.length >= 2) {
      return digits.slice(0, 2) + '/' + digits.slice(2);
    }
    return digits;
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!cardNumber || cardNumber.replace(/\s/g, '').length < 16) {
      newErrors.cardNumber = 'מספר כרטיס לא תקין';
    }
    if (!expiry || expiry.length < 5) {
      newErrors.expiry = 'תוקף לא תקין';
    }
    if (!cvv || cvv.length < 3) {
      newErrors.cvv = 'CVV לא תקין';
    }
    if (!cardholderName || cardholderName.trim().length < 2) {
      newErrors.cardholderName = 'נא להזין שם בעל הכרטיס';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    setStep('processing');

    // Simulate payment processing
    setTimeout(() => {
      setStep('success');
      setTimeout(() => {
        onPaymentSuccess();
      }, 1500);
    }, 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '20px',
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          width: '100%',
          maxWidth: '400px',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        {step === 'info' && (
          <div style={{ padding: '24px' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: colors.text, margin: 0 }}>
                הוספת פרטי תשלום
              </h2>
              <button
                onClick={onClose}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                }}
              >
                <X size={24} color={colors.gray} />
              </button>
            </div>

            {/* Info Icon */}
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: colors.primaryLight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
            }}>
              <CreditCard size={40} color={colors.primary} />
            </div>

            {/* Info Text */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: colors.text, marginBottom: '12px' }}>
                על מנת להוסיף ילד נא להוסיף פרטי תשלום
              </h3>
              <p style={{ fontSize: '14px', color: colors.textLight, lineHeight: '1.6' }}>
                כדי להוסיף ילדים למערכת, יש להזין פרטי כרטיס אשראי.
                <br />
                התשלום מאובטח באמצעות מערכת טרנזילה.
              </p>
            </div>

            {/* Price Info */}
            <div style={{
              backgroundColor: colors.grayLight,
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '14px', color: colors.textLight }}>מנוי חודשי</span>
                <span style={{ fontSize: '20px', fontWeight: 'bold', color: colors.primary }}>₪49.90</span>
              </div>
              <p style={{ fontSize: '12px', color: colors.textLight, marginTop: '8px', margin: '8px 0 0 0' }}>
                ניתן לבטל בכל עת
              </p>
            </div>

            {/* Continue Button */}
            <motion.button
              onClick={() => setStep('payment')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: '12px',
                backgroundColor: colors.primary,
                color: 'white',
                fontSize: '16px',
                fontWeight: '600',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              המשך לתשלום
              <CreditCard size={20} />
            </motion.button>
          </div>
        )}

        {step === 'payment' && (
          <div style={{ padding: '0' }}>
            {/* Tranzila-style Header */}
            <div style={{
              background: 'linear-gradient(135deg, #1a365d 0%, #2c5282 100%)',
              padding: '20px 24px',
              borderRadius: '20px 20px 0 0',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Lock size={18} color="white" />
                  <span style={{ color: 'white', fontSize: '14px' }}>תשלום מאובטח</span>
                </div>
                <button
                  onClick={onClose}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                  }}
                >
                  <X size={20} color="white" />
                </button>
              </div>
              <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '48px',
                  height: '32px',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  color: '#1a365d',
                }}>
                  TRANZILA
                </div>
                <div>
                  <div style={{ color: 'white', fontSize: '14px', fontWeight: '500' }}>קיפיקיד - מנוי חודשי</div>
                  <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '20px', fontWeight: 'bold' }}>₪49.90</div>
                </div>
              </div>
            </div>

            {/* Payment Form */}
            <div style={{ padding: '24px' }}>
              {/* Card Number */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: colors.text, marginBottom: '8px' }}>
                  מספר כרטיס
                </label>
                <input
                  type="text"
                  value={cardNumber}
                  onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                  placeholder="1234 5678 9012 3456"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '10px',
                    border: `1px solid ${errors.cardNumber ? colors.danger : colors.border}`,
                    fontSize: '16px',
                    direction: 'ltr',
                    textAlign: 'left',
                    boxSizing: 'border-box',
                  }}
                />
                {errors.cardNumber && (
                  <span style={{ fontSize: '12px', color: colors.danger, marginTop: '4px', display: 'block' }}>
                    {errors.cardNumber}
                  </span>
                )}
              </div>

              {/* Expiry and CVV */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: colors.text, marginBottom: '8px' }}>
                    תוקף
                  </label>
                  <input
                    type="text"
                    value={expiry}
                    onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                    placeholder="MM/YY"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '10px',
                      border: `1px solid ${errors.expiry ? colors.danger : colors.border}`,
                      fontSize: '16px',
                      direction: 'ltr',
                      textAlign: 'left',
                      boxSizing: 'border-box',
                    }}
                  />
                  {errors.expiry && (
                    <span style={{ fontSize: '12px', color: colors.danger, marginTop: '4px', display: 'block' }}>
                      {errors.expiry}
                    </span>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: colors.text, marginBottom: '8px' }}>
                    CVV
                  </label>
                  <input
                    type="text"
                    value={cvv}
                    onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    placeholder="123"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '10px',
                      border: `1px solid ${errors.cvv ? colors.danger : colors.border}`,
                      fontSize: '16px',
                      direction: 'ltr',
                      textAlign: 'left',
                      boxSizing: 'border-box',
                    }}
                  />
                  {errors.cvv && (
                    <span style={{ fontSize: '12px', color: colors.danger, marginTop: '4px', display: 'block' }}>
                      {errors.cvv}
                    </span>
                  )}
                </div>
              </div>

              {/* Cardholder Name */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: colors.text, marginBottom: '8px' }}>
                  שם בעל הכרטיס
                </label>
                <input
                  type="text"
                  value={cardholderName}
                  onChange={(e) => setCardholderName(e.target.value)}
                  placeholder="ישראל ישראלי"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '10px',
                    border: `1px solid ${errors.cardholderName ? colors.danger : colors.border}`,
                    fontSize: '16px',
                    boxSizing: 'border-box',
                  }}
                />
                {errors.cardholderName && (
                  <span style={{ fontSize: '12px', color: colors.danger, marginTop: '4px', display: 'block' }}>
                    {errors.cardholderName}
                  </span>
                )}
              </div>

              {/* Security Note */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px',
                backgroundColor: colors.successLight,
                borderRadius: '10px',
                marginBottom: '20px',
              }}>
                <Lock size={16} color={colors.success} />
                <span style={{ fontSize: '12px', color: colors.success }}>
                  התשלום מאובטח בתקן PCI DSS
                </span>
              </div>

              {/* Submit Button */}
              <motion.button
                onClick={handleSubmit}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                style={{
                  width: '100%',
                  padding: '14px',
                  borderRadius: '12px',
                  backgroundColor: '#22c55e',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                שלם ₪49.90
              </motion.button>

              {/* Back Button */}
              <button
                onClick={() => setStep('info')}
                style={{
                  width: '100%',
                  padding: '12px',
                  marginTop: '12px',
                  background: 'none',
                  border: 'none',
                  color: colors.textLight,
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                חזרה
              </button>
            </div>
          </div>
        )}

        {step === 'processing' && (
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                border: '4px solid ' + colors.grayLight,
                borderTopColor: colors.primary,
                margin: '0 auto 24px',
              }}
            />
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
              מעבד תשלום...
            </h3>
            <p style={{ fontSize: '14px', color: colors.textLight }}>
              נא לא לסגור את החלון
            </p>
          </div>
        )}

        {step === 'success' && (
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: colors.successLight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
              }}
            >
              <CheckCircle2 size={48} color={colors.success} />
            </motion.div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', color: colors.success, marginBottom: '8px' }}>
              התשלום בוצע בהצלחה!
            </h3>
            <p style={{ fontSize: '14px', color: colors.textLight }}>
              כעת תוכל להוסיף ילדים למערכת
            </p>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

// Child Card Component
function ChildCard({ child, index, onClick, onEdit }: { child: ChildData; index: number; onClick: () => void; onEdit: () => void }) {
  const hasArrived = !!child.arrivedAt;
  const canEdit = child.isPayingParent === true;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      onClick={onClick}
      style={{
        backgroundColor: 'white',
        borderRadius: '20px',
        padding: '20px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        cursor: 'pointer',
        border: `2px solid ${hasArrived ? colors.successLight : colors.warningLight}`,
        position: 'relative',
      }}
    >
      {/* Edit Button - only for paying parent */}
      {canEdit && (
      <motion.button
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        style={{
          position: 'absolute',
          top: '12px',
          left: '12px',
          padding: '8px',
          borderRadius: '10px',
          border: 'none',
          backgroundColor: colors.grayLight,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        whileHover={{ scale: 1.1, backgroundColor: colors.primaryLight }}
        whileTap={{ scale: 0.9 }}
      >
        <Edit3 size={16} color={colors.primary} />
      </motion.button>
      )}

      <div style={{ display: 'flex', gap: '16px' }}>
        {/* Photo */}
        <ChildAvatar
          photoUrl={child.photoUrl}
          firstName={child.firstName}
          lastName={child.lastName}
          size={80}
          borderRadius={20}
          borderWidth={3}
          borderColor={hasArrived ? colors.success : colors.warning}
        />

        {/* Info */}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text, marginBottom: '4px' }}>
                {child.firstName} {child.lastName}
              </h3>
              <p style={{ fontSize: '14px', color: colors.textLight, display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Building2 size={14} />
                {child.kindergartenName}
              </p>
            </div>

            {/* Status Badge */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              borderRadius: '20px',
              fontSize: '13px',
              fontWeight: '500',
              backgroundColor: hasArrived ? colors.successLight : colors.warningLight,
              color: hasArrived ? '#15803d' : '#92400e',
            }}>
              {hasArrived ? <CheckCircle2 size={14} /> : <Clock size={14} />}
              {hasArrived ? `הגיע ${child.arrivedAt}` : 'ממתין'}
            </div>
          </div>

          {/* Details Row */}
          <div style={{ display: 'flex', gap: '16px', marginTop: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: colors.textLight }}>
              <Clock size={14} color={colors.secondary} />
              <span>שעת הגעה להיום: {child.expectedTime}</span>
            </div>
{child.birthDate && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: colors.textLight }}>
                <User size={14} color={colors.primary} />
                <span>גיל: {calculateAge(child.birthDate)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Arrow */}
        <ChevronLeft size={20} color={colors.gray} style={{ alignSelf: 'center' }} />
      </div>
    </motion.div>
  );
}

// Child Details Modal
function ChildDetailsModal({ child, onClose }: { child: ChildData; onClose: () => void }) {
  const hasArrived = !!child.arrivedAt;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        zIndex: 100,
        padding: '16px',
      }}
    >
      <motion.div
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '24px 24px 0 0',
          padding: '24px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '80vh',
          overflow: 'auto',
        }}
      >
        {/* Handle */}
        <div style={{ width: '40px', height: '4px', backgroundColor: '#e5e7eb', borderRadius: '2px', margin: '0 auto 20px' }} />

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
          <ChildAvatar
            photoUrl={child.photoUrl}
            firstName={child.firstName}
            lastName={child.lastName}
            size={72}
            borderRadius={20}
            borderWidth={3}
            borderColor={hasArrived ? colors.success : colors.warning}
          />
          <div>
            <h2 style={{ fontSize: '22px', fontWeight: 'bold', color: colors.text }}>
              {child.firstName} {child.lastName}
            </h2>
{child.birthDate && (
              <p style={{ fontSize: '14px', color: colors.textLight }}>גיל {calculateAge(child.birthDate)}</p>
            )}
          </div>
        </div>

        {/* Kindergarten Info */}
        <div style={{
          backgroundColor: colors.secondaryLight,
          borderRadius: '16px',
          padding: '16px',
          marginBottom: '16px',
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.secondary, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Building2 size={18} />
            פרטי הגן
          </h3>
          <p style={{ fontSize: '15px', fontWeight: '500', color: colors.text, marginBottom: '8px' }}>{child.kindergartenName}</p>
          <p style={{ fontSize: '14px', color: colors.textLight, display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
            <MapPin size={14} />
            {child.kindergartenAddress}
          </p>
          <p style={{ fontSize: '14px', color: colors.textLight, display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Phone size={14} />
            {child.kindergartenPhone}
          </p>
        </div>

        {/* Quick Actions */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <motion.button
            style={{
              padding: '16px',
              backgroundColor: colors.primaryLight,
              color: colors.primary,
              border: 'none',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
            }}
            whileTap={{ scale: 0.95 }}
          >
            <Calendar size={24} />
            לוח שנה
          </motion.button>
          <motion.button
            style={{
              padding: '16px',
              backgroundColor: hasArrived ? colors.successLight : colors.warningLight,
              color: hasArrived ? colors.success : colors.warning,
              border: 'none',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
            }}
            whileTap={{ scale: 0.95 }}
          >
            {hasArrived ? <CheckCircle2 size={24} /> : <MapPin size={24} />}
            {hasArrived ? 'הגיע לגן' : 'סמן הגעה'}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Add Child Modal - UPDATED with all requested features
function AddChildModal({
  kindergartens,
  existingContacts,
  onClose,
  onAdd,
  onShowMessage,
}: {
  kindergartens: Kindergarten[];
  existingContacts: Contact[];
  onClose: () => void;
  onAdd: (child: ChildData) => void;
  onShowMessage: (type: 'success' | 'error' | 'warning' | 'info', title: string, message: string) => void;
}) {
  const [step, setStep] = useState(1); // 1: Personal + Photo, 2: Kindergarten, 3: Arrival Times, 4: Contacts & Alerts
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Step 1: Personal details
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [gender, setGender] = useState<number | null>(null); // 1 = male, 2 = female
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  // Step 2: Kindergarten search
  const [searchCity, setSearchCity] = useState('');
  const [searchStreet, setSearchStreet] = useState('');
  const [searchName, setSearchName] = useState('');
  const [selectedKindergarten, setSelectedKindergarten] = useState<Kindergarten | null>(null);

  // Step 3: Arrival times
  const [useAllDaysTime, setUseAllDaysTime] = useState(true);
  const [allDaysTime, setAllDaysTime] = useState('08:30');
  const [includeFriday, setIncludeFriday] = useState(false);
  const [showFridayQuestion, setShowFridayQuestion] = useState(false);
  const [arrivalTimes, setArrivalTimes] = useState<Record<string, string | null>>({
    sunday: '08:30',
    monday: '08:30',
    tuesday: '08:30',
    wednesday: '08:30',
    thursday: '08:30',
    friday: null,
  });

  // Step 4: Contacts and alerts
  const [childContacts, setChildContacts] = useState<Contact[]>([]);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [newContactName, setNewContactName] = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');
  const [newContactRelation, setNewContactRelation] = useState('');
  const [primaryAlertContact, setPrimaryAlertContact] = useState<number | null>(null);
  const [secondaryAlertContact, setSecondaryAlertContact] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false); // Prevent double submission

  // Filter kindergartens
  const filteredKindergartens = kindergartens.filter(k => {
    const matchCity = !searchCity || k.city.toLowerCase().includes(searchCity.toLowerCase());
    const matchStreet = !searchStreet || k.street.toLowerCase().includes(searchStreet.toLowerCase());
    const matchName = !searchName || k.name.toLowerCase().includes(searchName.toLowerCase());
    return matchCity && matchStreet && matchName;
  });

  // Handle image selection (mobile camera or gallery)
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle "all days" time change - triggers Friday question
  const handleAllDaysTimeChange = (time: string) => {
    setAllDaysTime(time);
    setShowFridayQuestion(true);
  };

  // Handle Friday question answer
  const handleFridayAnswer = (include: boolean) => {
    setIncludeFriday(include);
    setShowFridayQuestion(false);

    // Update all arrival times
    const newTimes: Record<string, string | null> = {};
    daysOfWeek.forEach(day => {
      if (day.key === 'friday') {
        newTimes[day.key] = include ? allDaysTime : null;
      } else {
        newTimes[day.key] = allDaysTime;
      }
    });
    setArrivalTimes(newTimes);
  };

  // Add contact from existing
  const addExistingContact = (contact: Contact) => {
    if (!childContacts.find(c => c.id === contact.id)) {
      setChildContacts([...childContacts, { ...contact, alertOrder: null }]);
    }
  };

  // Send invitation to new contact
  const sendInvitation = async () => {
    if (!newContactName || !newContactPhone) return;

    const newContact: Contact = {
      id: Date.now(),
      name: newContactName,
      phone: newContactPhone,
      relation: newContactRelation || 'איש קשר',
      isApproved: false, // Not approved until they accept invitation
      alertOrder: null,
    };

    setChildContacts([...childContacts, newContact]);

    // Create the invitation message
    const parentName = sessionStorage.getItem('kipikid-auth')
      ? JSON.parse(sessionStorage.getItem('kipikid-auth') || '{}')?.state?.user?.name || 'ההורה'
      : 'ההורה';
    const childFullName = firstName && lastName ? `${firstName} ${lastName}` : 'הילד/ה';
    const inviteMessage = `👶 קיפיקיד\n\n${parentName} מבקש/ת ממך להצטרף למערכת קיפיקיד לבקרה ואימות הגעה לגן של ${childFullName}.\n\nלהורדת האפליקציה: https://kipikid.app`;

    setNewContactName('');
    setNewContactPhone('');
    setNewContactRelation('');
    setShowAddContactModal(false);

    // Send invitation via API
    try {
      const token = useAuthStore.getState().token;
      const response = await fetch(`${API_BASE_URL}/invitations/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          phone: newContact.phone,
          message: inviteMessage,
          contactName: newContact.name,
          relation: newContact.relation,
        }),
      });

      if (response.ok) {
        onShowMessage('success', 'הזמנה נשלחה', `הזמנה נשלחה בהצלחה ל-${newContact.name} (${newContact.phone})`);
      } else {
        // API failed, show message anyway (for dev mode)
        console.log('Invitation API not available, showing message only');
        onShowMessage('info', 'הזמנה נשלחה', `הזמנה נשלחה ל-${newContact.phone}`);
      }
    } catch (error) {
      // Network error, show message anyway (for dev mode)
      console.log('Could not send invitation via API:', error);
      onShowMessage('info', 'הזמנה נשלחה', `הזמנה נשלחה ל-${newContact.phone}`);
    }
  };

  // Set alert order - only for approved contacts
  // Alert 1 = receives both alerts (first AND second), Alert 2 = receives only second alert
  const setAlertOrder = (contactId: number, order: 1 | 2 | null) => {
    const contact = childContacts.find(c => c.id === contactId);
    if (!contact?.isApproved && order !== null) {
      onShowMessage('warning', 'לא ניתן', 'ניתן להגדיר התראות רק לאנשי קשר מאושרים');
      return;
    }

    // If clicking the same order again, toggle it off
    const currentOrder = contact?.alertOrder;
    const newOrder = currentOrder === order ? null : order;

    // Update the contact's alert order
    const updatedContacts = childContacts.map(c => {
      if (c.id === contactId) {
        return { ...c, alertOrder: newOrder };
      }
      // Remove this order from other contacts (only if it's the same order being set)
      if (newOrder !== null && c.alertOrder === newOrder) {
        return { ...c, alertOrder: null };
      }
      return c;
    });

    setChildContacts(updatedContacts);

    // Update the primary/secondary alert contact state
    if (newOrder === 1) {
      // When setting alert 1, also set as secondary (receives both alerts)
      setPrimaryAlertContact(contactId);
      setSecondaryAlertContact(contactId);
    } else if (newOrder === null && primaryAlertContact === contactId) {
      setPrimaryAlertContact(null);
      // Also remove from secondary if it was set
      if (secondaryAlertContact === contactId) {
        setSecondaryAlertContact(null);
      }
    }

    if (newOrder === 2) {
      setSecondaryAlertContact(contactId);
    } else if (newOrder === null && secondaryAlertContact === contactId) {
      setSecondaryAlertContact(null);
    }
  };

  const handleSubmit = async () => {
    // Prevent double submission
    if (isSubmitting) return;

    if (!firstName || !lastName || !selectedKindergarten) {
      onShowMessage('warning', 'שדות חסרים', 'יש למלא שם פרטי, שם משפחה ולבחור גן');
      return;
    }

    // Validate arrival times against kindergarten limits
    const arrivalError = validateArrivalTimes(selectedKindergarten, arrivalTimes);
    if (arrivalError) {
      onShowMessage('warning', 'שעת הגעה חורגת', arrivalError);
      return;
    }

    // Validate that at least one approved contact has alert order 1 (receives both alerts)
    const approvedContacts = childContacts.filter(c => c.isApproved);
    const hasAlert1 = primaryAlertContact !== null || approvedContacts.some(c => c.alertOrder === 1);

    if (approvedContacts.length > 0 && !hasAlert1) {
      onShowMessage('warning', 'חסר איש קשר להתראה', 'חובה לבחור לפחות איש קשר אחד שיקבל את שתי ההתראות (התראה 1)');
      return;
    }

    // Build the contacts with correct alert orders
    const finalContacts = childContacts.map(c => ({
      ...c,
      alertOrder: c.id === primaryAlertContact ? 1 : c.id === secondaryAlertContact ? 2 : c.alertOrder,
    }));

    // Create new child data for the list
    const newChild: ChildData = {
      id: Date.now(),
      publicId: `local-${Date.now()}`, // Mark as locally created child
      firstName,
      lastName,
      photoUrl: photoUrl || undefined,
      kindergartenName: selectedKindergarten.name,
      kindergartenAddress: `${selectedKindergarten.street} ${selectedKindergarten.houseNumber || ''}, ${selectedKindergarten.city}`,
      kindergartenPhone: '', // Would come from API
      arrivedAt: undefined,
      expectedTime: getTodayArrivalTime(
        arrivalTimes.sunday, arrivalTimes.monday, arrivalTimes.tuesday,
        arrivalTimes.wednesday, arrivalTimes.thursday, arrivalTimes.friday
      ) || arrivalTimes.sunday || '08:30',
      birthDate: undefined, // Will be set when child has birthDate
    };

    const childDataForApi = {
      firstName,
      lastName,
      idNumber: idNumber || undefined,
      birthDate: birthDate || undefined,
      gender: gender || undefined,
      photoUrl,
      kindergartenPublicId: selectedKindergarten.publicId,
      kindergartenName: selectedKindergarten.name,
      // Map arrival times to API format
      sundayArrivalTime: arrivalTimes.sunday || undefined,
      mondayArrivalTime: arrivalTimes.monday || undefined,
      tuesdayArrivalTime: arrivalTimes.tuesday || undefined,
      wednesdayArrivalTime: arrivalTimes.wednesday || undefined,
      thursdayArrivalTime: arrivalTimes.thursday || undefined,
      fridayArrivalTime: arrivalTimes.friday || undefined,
      contacts: finalContacts,
    };

    setIsSubmitting(true);
    try {
      // Try API call first
      const response = await fetch(`${API_BASE_URL}/children`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${useAuthStore.getState().token}`,
        },
        body: JSON.stringify(childDataForApi),
      });

      if (response.ok) {
        const data = await response.json();
        // Use the ID and publicId from the API response if available
        newChild.id = data.id || newChild.id;
        newChild.publicId = data.publicId || data.data?.publicId || newChild.publicId;
        onAdd(newChild);
        onShowMessage('success', 'נוסף בהצלחה', `${firstName} ${lastName} נוסף/ה בהצלחה!`);
        onClose(); // Close modal after success
      } else {
        const errorData = await response.json().catch(() => ({}));
        onShowMessage('error', 'שגיאה', errorData.message || 'שגיאה בהוספת ילד');
      }
    } catch (err) {
      console.error('Error adding child:', err);
      onShowMessage('error', 'שגיאה', 'שגיאה בהוספת ילד לשרת');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 14px',
    fontSize: '15px',
    border: `2px solid ${colors.border}`,
    borderRadius: '10px',
    outline: 'none',
    boxSizing: 'border-box' as const,
    direction: 'rtl' as const,
  };

  const labelStyle = {
    fontSize: '13px',
    fontWeight: '500' as const,
    color: colors.text,
    marginBottom: '6px',
    display: 'block',
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onMouseDown={(e) => {
        // Only close if clicking directly on backdrop, not when dragging from inside
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '24px 24px 0 0',
          padding: '20px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        {/* Hidden file inputs for mobile */}
        <input type="file" ref={fileInputRef} onChange={handleImageSelect} accept="image/*" style={{ display: 'none' }} />
        <input type="file" ref={cameraInputRef} onChange={handleImageSelect} accept="image/*" capture="environment" style={{ display: 'none' }} />

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text }}>
            הוספת ילד/ה ({step}/4)
          </h2>
          <motion.button
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: colors.grayLight,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            whileTap={{ scale: 0.9 }}
          >
            <X size={18} color={colors.gray} />
          </motion.button>
        </div>

        {/* Step Indicator */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
          {[1, 2, 3, 4].map(s => (
            <div
              key={s}
              style={{
                flex: 1,
                height: '4px',
                borderRadius: '2px',
                backgroundColor: s <= step ? colors.secondary : colors.border,
                transition: 'background-color 0.3s',
              }}
            />
          ))}
        </div>

        {/* Step 1: Personal Details + Photo Upload */}
        {step === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Photo Upload - Mobile Friendly */}
            <div style={{ textAlign: 'center' }}>
              <label style={{ ...labelStyle, textAlign: 'center' }}>תמונה</label>
              <div style={{
                width: '100px',
                height: '100px',
                margin: '0 auto 12px',
                borderRadius: '20px',
                overflow: 'hidden',
                backgroundColor: colors.grayLight,
                border: `2px dashed ${colors.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {photoUrl ? (
                  <img src={photoUrl} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <User size={40} color={colors.textLight} />
                )}
              </div>

              {/* Upload buttons - Camera and Gallery */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <motion.button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '10px 16px',
                    backgroundColor: colors.primaryLight,
                    color: colors.primary,
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Camera size={16} />
                  צלם
                </motion.button>

                <motion.button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '10px 16px',
                    backgroundColor: colors.secondaryLight,
                    color: colors.secondary,
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  <ImageIcon size={16} />
                  גלריה
                </motion.button>
              </div>

              {photoUrl && (
                <button
                  type="button"
                  onClick={() => setPhotoUrl(null)}
                  style={{
                    marginTop: '8px',
                    padding: '6px 12px',
                    backgroundColor: 'transparent',
                    color: '#dc2626',
                    border: 'none',
                    fontSize: '12px',
                    cursor: 'pointer',
                  }}
                >
                  הסר תמונה
                </button>
              )}
            </div>

            {/* Name fields */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>שם פרטי *</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="שם פרטי"
                  style={inputStyle}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>שם משפחה *</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="שם משפחה"
                  style={inputStyle}
                />
              </div>
            </div>

            {/* ID Number */}
            <div>
              <label style={labelStyle}>תעודת זהות *</label>
              <input
                type="text"
                value={idNumber}
                onChange={(e) => setIdNumber(e.target.value.replace(/\D/g, ''))}
                placeholder="מספר תעודת זהות"
                maxLength={9}
                style={{ ...inputStyle, direction: 'ltr', textAlign: 'right' }}
              />
            </div>

            {/* Birth Date and Gender */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>תאריך לידה *</label>
                <input
                  type="date"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  style={{ ...inputStyle, direction: 'ltr', textAlign: 'right' }}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>מין *</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <motion.button
                    type="button"
                    onClick={() => setGender(1)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '10px',
                      border: `2px solid ${gender === 1 ? colors.primary : colors.border}`,
                      backgroundColor: gender === 1 ? colors.primaryLight : 'white',
                      color: gender === 1 ? colors.primary : colors.text,
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                    }}
                    whileTap={{ scale: 0.95 }}
                  >
                    בן 👦
                  </motion.button>
                  <motion.button
                    type="button"
                    onClick={() => setGender(2)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '10px',
                      border: `2px solid ${gender === 2 ? colors.secondary : colors.border}`,
                      backgroundColor: gender === 2 ? colors.secondaryLight : 'white',
                      color: gender === 2 ? colors.secondary : colors.text,
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                    }}
                    whileTap={{ scale: 0.95 }}
                  >
                    בת 👧
                  </motion.button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Kindergarten Selection with search */}
        {step === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <p style={{ fontSize: '13px', color: colors.textLight }}>
              חפש גן לפי שם, עיר או רחוב
            </p>

            {/* Search by name */}
            <div>
              <label style={labelStyle}>שם הגן</label>
              <input
                type="text"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                placeholder="חיפוש לפי שם הגן"
                style={inputStyle}
              />
            </div>

            {/* Search by city and street */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>עיר</label>
                <input
                  type="text"
                  value={searchCity}
                  onChange={(e) => setSearchCity(e.target.value)}
                  placeholder="חיפוש לפי עיר"
                  style={inputStyle}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>רחוב</label>
                <input
                  type="text"
                  value={searchStreet}
                  onChange={(e) => setSearchStreet(e.target.value)}
                  placeholder="חיפוש לפי רחוב"
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Results */}
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {filteredKindergartens.length > 0 ? (
                filteredKindergartens.map(k => (
                  <motion.div
                    key={k.publicId}
                    onClick={() => setSelectedKindergarten(k)}
                    style={{
                      padding: '14px',
                      borderRadius: '12px',
                      marginBottom: '8px',
                      border: `2px solid ${selectedKindergarten?.publicId === k.publicId ? colors.secondary : colors.border}`,
                      backgroundColor: selectedKindergarten?.publicId === k.publicId ? colors.secondaryLight : 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div>
                      <p style={{ fontWeight: '600', color: colors.text, marginBottom: '4px' }}>{k.name}</p>
                      <p style={{ fontSize: '12px', color: colors.textLight }}>
                        {k.street} {k.houseNumber}, {k.city}
                      </p>
                    </div>
                    {selectedKindergarten?.publicId === k.publicId && <CheckCircle2 size={20} color={colors.secondary} />}
                  </motion.div>
                ))
              ) : (
                <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
                  <Building2 size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
                  <p>לא נמצאו גנים</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Arrival Times with "all days" option and days selection */}
        {step === 3 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <p style={{ fontSize: '13px', color: colors.textLight }}>
              הגדר שעת הגעה אחרונה לכל יום
            </p>

            {/* Kindergarten max arrival time hint */}
            {selectedKindergarten?.operatingDays && selectedKindergarten.operatingDays.length > 0 && (() => {
              const maxTime = getEarliestMaxArrivalTime(selectedKindergarten, arrivalTimes);
              return maxTime ? (
                <div style={{ padding: '8px 12px', borderRadius: '8px', backgroundColor: '#fef3c7', border: '1px solid #f59e0b' }}>
                  <p style={{ fontSize: '12px', color: '#92400e', textAlign: 'center' }}>
                    ⏰ שעת הגעה אחרונה לגן: <strong>{maxTime}</strong>
                  </p>
                </div>
              ) : null;
            })()}

            {/* Toggle: All days vs individual */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <motion.button
                type="button"
                onClick={() => setUseAllDaysTime(true)}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '10px',
                  border: `2px solid ${useAllDaysTime ? colors.secondary : colors.border}`,
                  backgroundColor: useAllDaysTime ? colors.secondaryLight : 'white',
                  color: useAllDaysTime ? colors.secondary : colors.text,
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
                whileTap={{ scale: 0.98 }}
              >
                שעה אחת לכל הימים
              </motion.button>
              <motion.button
                type="button"
                onClick={() => setUseAllDaysTime(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '10px',
                  border: `2px solid ${!useAllDaysTime ? colors.secondary : colors.border}`,
                  backgroundColor: !useAllDaysTime ? colors.secondaryLight : 'white',
                  color: !useAllDaysTime ? colors.secondary : colors.text,
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
                whileTap={{ scale: 0.98 }}
              >
                שעה שונה לכל יום
              </motion.button>
            </div>

            {/* All days time input with dropdown selects */}
            {useAllDaysTime && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {/* Days selection: א-ה or א-ו */}
                <div>
                  <label style={labelStyle}>ימי פעילות</label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <motion.button
                      type="button"
                      onClick={() => {
                        setIncludeFriday(false);
                        setShowFridayQuestion(false);
                        const newTimes: Record<string, string | null> = {};
                        daysOfWeek.forEach(day => {
                          newTimes[day.key] = day.key === 'friday' ? null : allDaysTime;
                        });
                        setArrivalTimes(newTimes);
                      }}
                      style={{
                        flex: 1,
                        padding: '12px',
                        borderRadius: '10px',
                        border: `2px solid ${!includeFriday ? colors.primary : colors.border}`,
                        backgroundColor: !includeFriday ? colors.primaryLight : 'white',
                        color: !includeFriday ? colors.primary : colors.text,
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                      }}
                      whileTap={{ scale: 0.98 }}
                    >
                      א' - ה'
                    </motion.button>
                    <motion.button
                      type="button"
                      onClick={() => {
                        setIncludeFriday(true);
                        setShowFridayQuestion(false);
                        const newTimes: Record<string, string | null> = {};
                        daysOfWeek.forEach(day => {
                          newTimes[day.key] = allDaysTime;
                        });
                        setArrivalTimes(newTimes);
                      }}
                      style={{
                        flex: 1,
                        padding: '12px',
                        borderRadius: '10px',
                        border: `2px solid ${includeFriday ? colors.primary : colors.border}`,
                        backgroundColor: includeFriday ? colors.primaryLight : 'white',
                        color: includeFriday ? colors.primary : colors.text,
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                      }}
                      whileTap={{ scale: 0.98 }}
                    >
                      א' - ו'
                    </motion.button>
                  </div>
                </div>

                {/* Time picker with dropdowns - RTL order: minutes on right, hours on left */}
                <div>
                  <label style={labelStyle}>שעת הגעה אחרונה</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center', direction: 'ltr' }}>
                    <select
                      value={allDaysTime.split(':')[0] || '08'}
                      onChange={(e) => {
                        const minutes = allDaysTime.split(':')[1] || '00';
                        const newTime = `${e.target.value}:${minutes}`;
                        setAllDaysTime(newTime);
                        // Update arrival times
                        const newTimes: Record<string, string | null> = {};
                        daysOfWeek.forEach(day => {
                          if (day.key === 'friday') {
                            newTimes[day.key] = includeFriday ? newTime : null;
                          } else {
                            newTimes[day.key] = newTime;
                          }
                        });
                        setArrivalTimes(newTimes);
                      }}
                      style={{
                        padding: '14px 20px',
                        fontSize: '20px',
                        fontWeight: '600',
                        border: `2px solid ${colors.border}`,
                        borderRadius: '12px',
                        backgroundColor: 'white',
                        textAlign: 'center',
                        width: '90px',
                      }}
                    >
                      {Array.from({ length: 13 }, (_, i) => i + 6).map(hour => (
                        <option key={hour} value={hour.toString().padStart(2, '0')}>
                          {hour.toString().padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                    <span style={{ fontSize: '24px', fontWeight: 'bold', color: colors.text }}>:</span>
                    <select
                      value={allDaysTime.split(':')[1] || '00'}
                      onChange={(e) => {
                        const hours = allDaysTime.split(':')[0] || '08';
                        const newTime = `${hours}:${e.target.value}`;
                        setAllDaysTime(newTime);
                        // Update arrival times
                        const newTimes: Record<string, string | null> = {};
                        daysOfWeek.forEach(day => {
                          if (day.key === 'friday') {
                            newTimes[day.key] = includeFriday ? newTime : null;
                          } else {
                            newTimes[day.key] = newTime;
                          }
                        });
                        setArrivalTimes(newTimes);
                      }}
                      style={{
                        padding: '14px 20px',
                        fontSize: '20px',
                        fontWeight: '600',
                        border: `2px solid ${colors.border}`,
                        borderRadius: '12px',
                        backgroundColor: 'white',
                        textAlign: 'center',
                        width: '90px',
                      }}
                    >
                      {['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map(minute => (
                        <option key={minute} value={minute}>
                          {minute}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Summary */}
                <div style={{ padding: '12px', borderRadius: '10px', backgroundColor: colors.grayLight }}>
                  <p style={{ fontSize: '13px', color: colors.textLight, textAlign: 'center' }}>
                    שעת הגעה: {allDaysTime} | ימים: {includeFriday ? "א'-ו'" : "א'-ה'"}
                  </p>
                </div>
              </div>
            )}

            {/* Individual day inputs - RTL order: hours:minutes from left to right */}
            {!useAllDaysTime && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {daysOfWeek.map(day => (
                  <div key={day.key} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ width: '60px', fontSize: '14px', fontWeight: '500', color: colors.text }}>
                      {day.fullLabel}
                    </span>
                    <div style={{ display: 'flex', gap: '4px', flex: 1, direction: 'ltr' }}>
                      <select
                        value={arrivalTimes[day.key]?.split(':')[0] || '08'}
                        onChange={(e) => {
                          const currentTime = arrivalTimes[day.key] || '08:00';
                          const minutes = currentTime.split(':')[1] || '00';
                          setArrivalTimes({ ...arrivalTimes, [day.key]: `${e.target.value}:${minutes}` });
                        }}
                        disabled={!arrivalTimes[day.key]}
                        style={{
                          padding: '10px',
                          fontSize: '16px',
                          fontWeight: '600',
                          border: `2px solid ${colors.border}`,
                          borderRadius: '8px',
                          backgroundColor: arrivalTimes[day.key] ? 'white' : colors.grayLight,
                          flex: 1,
                          textAlign: 'center',
                        }}
                      >
                        {Array.from({ length: 13 }, (_, i) => i + 6).map(hour => (
                          <option key={hour} value={hour.toString().padStart(2, '0')}>
                            {hour.toString().padStart(2, '0')}
                          </option>
                        ))}
                      </select>
                      <span style={{ alignSelf: 'center', fontWeight: 'bold' }}>:</span>
                      <select
                        value={arrivalTimes[day.key]?.split(':')[1] || '00'}
                        onChange={(e) => {
                          const currentTime = arrivalTimes[day.key] || '08:00';
                          const hours = currentTime.split(':')[0] || '08';
                          setArrivalTimes({ ...arrivalTimes, [day.key]: `${hours}:${e.target.value}` });
                        }}
                        disabled={!arrivalTimes[day.key]}
                        style={{
                          padding: '10px',
                          fontSize: '16px',
                          fontWeight: '600',
                          border: `2px solid ${colors.border}`,
                          borderRadius: '8px',
                          backgroundColor: arrivalTimes[day.key] ? 'white' : colors.grayLight,
                          flex: 1,
                          textAlign: 'center',
                        }}
                      >
                        {['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map(minute => (
                          <option key={minute} value={minute}>
                            {minute}
                          </option>
                        ))}
                      </select>
                    </div>
                    <motion.button
                      type="button"
                      onClick={() => setArrivalTimes({ ...arrivalTimes, [day.key]: arrivalTimes[day.key] ? null : '08:30' })}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '8px',
                        border: 'none',
                        backgroundColor: arrivalTimes[day.key] ? colors.successLight : colors.grayLight,
                        color: arrivalTimes[day.key] ? colors.success : colors.textLight,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {arrivalTimes[day.key] ? <CheckCircle2 size={18} /> : <X size={18} />}
                    </motion.button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 4: Contacts and Alerts */}
        {step === 4 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Instructions */}
            <div style={{
              padding: '12px',
              borderRadius: '10px',
              backgroundColor: colors.secondaryLight,
              fontSize: '13px',
              color: colors.secondary,
            }}>
              <p style={{ fontWeight: '600', marginBottom: '4px' }}>תהליך הגדרת התראות:</p>
              <ol style={{ margin: 0, paddingRight: '16px', lineHeight: '1.6' }}>
                <li>בחר אנשי קשר קיימים או שלח הזמנה לחדשים</li>
                <li>רק לאחר אישור איש הקשר ניתן לשייך אותו לילד</li>
                <li>הגדר מי מקבל התראה ראשונה ושנייה</li>
              </ol>
              <p style={{ marginTop: '8px', fontSize: '12px' }}>
                * מי שמקבל התראה ראשונה מקבל גם את השנייה
              </p>
            </div>

            {/* Existing contacts */}
            <div>
              <label style={labelStyle}>אנשי קשר קיימים</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {existingContacts.map(contact => {
                  const isAdded = childContacts.find(c => c.id === contact.id);
                  return (
                    <motion.button
                      key={contact.id}
                      type="button"
                      onClick={() => !isAdded && addExistingContact(contact)}
                      style={{
                        padding: '8px 14px',
                        borderRadius: '8px',
                        border: `2px solid ${isAdded ? colors.success : colors.border}`,
                        backgroundColor: isAdded ? colors.successLight : 'white',
                        color: isAdded ? colors.success : colors.text,
                        fontSize: '13px',
                        cursor: isAdded ? 'default' : 'pointer',
                        opacity: isAdded ? 0.7 : 1,
                      }}
                      whileTap={isAdded ? {} : { scale: 0.98 }}
                    >
                      {contact.name} ({contact.relation})
                      {isAdded && <CheckCircle2 size={14} style={{ marginRight: '4px' }} />}
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* Add new contact button */}
            <motion.button
              type="button"
              onClick={() => setShowAddContactModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '12px',
                borderRadius: '10px',
                border: `2px dashed ${colors.border}`,
                backgroundColor: 'white',
                color: colors.primary,
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
              whileTap={{ scale: 0.98 }}
            >
              <UserPlus size={18} />
              שלח הזמנה לאיש קשר חדש
            </motion.button>

            {/* Selected contacts with alert settings */}
            {childContacts.length > 0 && (
              <div>
                <label style={labelStyle}>אנשי קשר משויכים והתראות</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {childContacts.map(contact => (
                    <div
                      key={contact.id}
                      style={{
                        padding: '14px',
                        borderRadius: '12px',
                        backgroundColor: colors.grayLight,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <p style={{ fontSize: '14px', fontWeight: '500', color: colors.text }}>
                            {contact.name}
                          </p>
                          {!contact.isApproved && (
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '10px',
                              backgroundColor: colors.warningLight,
                              color: '#92400e',
                            }}>
                              ממתין לאישור
                            </span>
                          )}
                        </div>
                        <p style={{ fontSize: '12px', color: colors.textLight }}>
                          {contact.relation} | {contact.phone}
                        </p>
                      </div>

                      {/* Alert buttons - only for approved contacts */}
                      {contact.isApproved && (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <motion.button
                            type="button"
                            onClick={() => setAlertOrder(contact.id, primaryAlertContact === contact.id ? null : 1)}
                            style={{
                              padding: '6px 10px',
                              borderRadius: '6px',
                              border: 'none',
                              backgroundColor: primaryAlertContact === contact.id ? colors.warning : colors.border,
                              color: primaryAlertContact === contact.id ? 'white' : colors.textLight,
                              fontSize: '11px',
                              fontWeight: '500',
                              cursor: 'pointer',
                            }}
                            whileTap={{ scale: 0.95 }}
                          >
                            התראה 1
                          </motion.button>
                          <motion.button
                            type="button"
                            onClick={() => setAlertOrder(contact.id, secondaryAlertContact === contact.id ? null : 2)}
                            style={{
                              padding: '6px 10px',
                              borderRadius: '6px',
                              border: 'none',
                              // Alert 2 is highlighted if explicitly set OR if this contact has Alert 1 (receives both)
                              backgroundColor: (secondaryAlertContact === contact.id || primaryAlertContact === contact.id) ? colors.secondary : colors.border,
                              color: (secondaryAlertContact === contact.id || primaryAlertContact === contact.id) ? 'white' : colors.textLight,
                              fontSize: '11px',
                              fontWeight: '500',
                              cursor: primaryAlertContact === contact.id ? 'not-allowed' : 'pointer',
                              opacity: primaryAlertContact === contact.id ? 0.7 : 1,
                            }}
                            whileTap={{ scale: 0.95 }}
                            disabled={primaryAlertContact === contact.id}
                            title={primaryAlertContact === contact.id ? 'מי שמקבל התראה ראשונה מקבל גם את השנייה' : ''}
                          >
                            התראה 2
                          </motion.button>
                        </div>
                      )}

                      {/* Remove button */}
                      <motion.button
                        type="button"
                        onClick={() => {
                          setChildContacts(childContacts.filter(c => c.id !== contact.id));
                          if (primaryAlertContact === contact.id) setPrimaryAlertContact(null);
                          if (secondaryAlertContact === contact.id) setSecondaryAlertContact(null);
                        }}
                        style={{
                          marginRight: '8px',
                          padding: '6px',
                          borderRadius: '6px',
                          border: 'none',
                          backgroundColor: 'transparent',
                          color: '#dc2626',
                          cursor: 'pointer',
                        }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <X size={16} />
                      </motion.button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add new contact modal */}
            <AnimatePresence>
              {showAddContactModal && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 110,
                    padding: '16px',
                  }}
                  onMouseDown={(e) => {
                    if (e.target === e.currentTarget) {
                      setShowAddContactModal(false);
                    }
                  }}
                >
                  <motion.div
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0.9 }}
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '16px',
                      padding: '20px',
                      width: '100%',
                      maxWidth: '350px',
                    }}
                  >
                    <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text, marginBottom: '16px' }}>
                      שלח הזמנה לאיש קשר חדש
                    </h3>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div>
                        <label style={labelStyle}>שם *</label>
                        <input
                          type="text"
                          value={newContactName}
                          onChange={(e) => setNewContactName(e.target.value)}
                          placeholder="שם מלא"
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>טלפון *</label>
                        <input
                          type="tel"
                          value={newContactPhone}
                          onChange={(e) => setNewContactPhone(e.target.value)}
                          placeholder="050-1234567"
                          style={{ ...inputStyle, direction: 'ltr', textAlign: 'right' }}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>קרבה</label>
                        <input
                          type="text"
                          value={newContactRelation}
                          onChange={(e) => setNewContactRelation(e.target.value)}
                          placeholder="אמא, אבא, סבתא..."
                          style={inputStyle}
                        />
                      </div>

                      {/* Message Preview */}
                      {newContactPhone && (
                        <div style={{
                          padding: '12px',
                          borderRadius: '10px',
                          backgroundColor: colors.secondaryLight,
                          border: `1px solid ${colors.secondary}`,
                        }}>
                          <p style={{ fontSize: '12px', fontWeight: '600', color: colors.secondary, marginBottom: '6px' }}>
                            תוכן ההודעה שתשלח:
                          </p>
                          <p style={{ fontSize: '13px', color: colors.text, lineHeight: '1.5' }}>
                            "ההורה מבקש/ת ממך להצטרף למערכת קיפיקיד לבקרה ואימות הגעה לגן של {firstName && lastName ? `${firstName} ${lastName}` : 'הילד/ה'}"
                          </p>
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                      <motion.button
                        type="button"
                        onClick={() => setShowAddContactModal(false)}
                        style={{
                          flex: 1,
                          padding: '12px',
                          borderRadius: '10px',
                          backgroundColor: colors.grayLight,
                          color: colors.text,
                          border: 'none',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                        }}
                        whileTap={{ scale: 0.98 }}
                      >
                        ביטול
                      </motion.button>
                      <motion.button
                        type="button"
                        onClick={sendInvitation}
                        disabled={!newContactName || !newContactPhone}
                        style={{
                          flex: 1,
                          padding: '12px',
                          borderRadius: '10px',
                          backgroundColor: colors.primary,
                          color: 'white',
                          border: 'none',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: newContactName && newContactPhone ? 'pointer' : 'not-allowed',
                          opacity: newContactName && newContactPhone ? 1 : 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                        }}
                        whileTap={newContactName && newContactPhone ? { scale: 0.98 } : {}}
                      >
                        <Send size={16} />
                        שלח הזמנה
                      </motion.button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Navigation Buttons */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '24px' }}>
          {step > 1 && (
            <motion.button
              type="button"
              onClick={() => setStep(step - 1)}
              style={{
                flex: 1,
                padding: '14px',
                borderRadius: '12px',
                backgroundColor: colors.grayLight,
                color: colors.text,
                fontSize: '15px',
                fontWeight: '600',
                border: 'none',
                cursor: 'pointer',
              }}
              whileTap={{ scale: 0.98 }}
            >
              הקודם
            </motion.button>
          )}

          <motion.button
            type="button"
            onClick={() => {
              if (step < 4) {
                if (step === 1 && (!firstName || !lastName || !idNumber || !birthDate || !gender)) {
                  onShowMessage('warning', 'שדות חסרים', 'יש למלא את כל השדות: שם פרטי, שם משפחה, תעודת זהות, תאריך לידה ומין');
                  return;
                }
                if (step === 2 && !selectedKindergarten) {
                  onShowMessage('warning', 'שדות חסרים', 'יש לבחור גן');
                  return;
                }
                if (step === 3 && showFridayQuestion) {
                  onShowMessage('warning', 'שדות חסרים', 'יש לענות על השאלה לגבי יום שישי');
                  return;
                }
                setStep(step + 1);
              } else {
                handleSubmit();
              }
            }}
            disabled={isSubmitting}
            style={{
              flex: step > 1 ? 1 : undefined,
              width: step === 1 ? '100%' : undefined,
              padding: '14px',
              borderRadius: '12px',
              background: isSubmitting
                ? colors.gray
                : `linear-gradient(135deg, ${colors.secondary} 0%, #0d9488 100%)`,
              color: 'white',
              fontSize: '15px',
              fontWeight: '600',
              border: 'none',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.7 : 1,
            }}
            whileTap={isSubmitting ? {} : { scale: 0.98 }}
          >
            {isSubmitting ? 'שומר...' : (step < 4 ? 'הבא' : 'הוסף ילד/ה')}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Edit Child Modal
function EditChildModal({
  child,
  kindergartens,
  onClose,
  onUpdate,
  onDelete,
  onShowMessage,
}: {
  child: ChildData;
  kindergartens: Kindergarten[];
  onClose: () => void;
  onUpdate: (child: ChildData) => void;
  onDelete: (childId: number) => void;
  onShowMessage: (type: 'success' | 'error' | 'warning' | 'info', title: string, message: string) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Form state - pre-filled with existing data
  const [firstName, setFirstName] = useState(child.firstName);
  const [lastName, setLastName] = useState(child.lastName);
  const [photoUrl, setPhotoUrl] = useState<string | undefined>(child.photoUrl);

  // Arrival times - initialize from child's per-day fields
  const initArrivalTimes: Record<string, string | null> = {
    sunday: child.sundayArrivalTime || child.expectedTime || '08:30',
    monday: child.mondayArrivalTime || child.expectedTime || '08:30',
    tuesday: child.tuesdayArrivalTime || child.expectedTime || '08:30',
    wednesday: child.wednesdayArrivalTime || child.expectedTime || '08:30',
    thursday: child.thursdayArrivalTime || child.expectedTime || '08:30',
    friday: child.fridayArrivalTime || null,
  };

  // Detect if all non-null times are uniform
  const nonNullTimes = Object.values(initArrivalTimes).filter(t => t !== null) as string[];
  const allSame = nonNullTimes.length > 0 && nonNullTimes.every(t => t === nonNullTimes[0]);

  const [useAllDaysTime, setUseAllDaysTime] = useState(allSame);
  const [allDaysTime, setAllDaysTime] = useState(allSame ? nonNullTimes[0] : (child.expectedTime || '08:30'));
  const [includeFriday, setIncludeFriday] = useState(child.fridayArrivalTime != null);
  const [arrivalTimes, setArrivalTimes] = useState<Record<string, string | null>>(initArrivalTimes);

  // Kindergarten - prefer matching by PublicId, fallback to name
  const [searchKindergarten, setSearchKindergarten] = useState('');
  const [selectedKindergarten, setSelectedKindergarten] = useState<Kindergarten | null>(
    child.kindergartenPublicId
      ? kindergartens.find(k => k.publicId === child.kindergartenPublicId) || null
      : kindergartens.find(k => k.name === child.kindergartenName) || null
  );
  const [showKindergartenSearch, setShowKindergartenSearch] = useState(false);

  const filteredKindergartens = kindergartens.filter(k =>
    k.name.toLowerCase().includes(searchKindergarten.toLowerCase()) ||
    k.city.toLowerCase().includes(searchKindergarten.toLowerCase())
  );

  // Handle image selection
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // The effective kindergarten for validation (selected or original)
  const effectiveKindergarten = selectedKindergarten || kindergartens.find(k => k.publicId === child.kindergartenPublicId) || null;

  const handleSubmit = () => {
    if (!firstName || !lastName) {
      onShowMessage('warning', 'שדות חסרים', 'יש למלא שם פרטי ושם משפחה');
      return;
    }

    // Validate arrival times against kindergarten limits
    const arrivalError = validateArrivalTimes(effectiveKindergarten, arrivalTimes);
    if (arrivalError) {
      onShowMessage('warning', 'שעת הגעה חורגת', arrivalError);
      return;
    }

    const updatedChild: ChildData = {
      ...child,
      firstName,
      lastName,
      photoUrl,
      expectedTime: getTodayArrivalTime(
        arrivalTimes.sunday, arrivalTimes.monday, arrivalTimes.tuesday,
        arrivalTimes.wednesday, arrivalTimes.thursday, arrivalTimes.friday
      ) || arrivalTimes.sunday || allDaysTime || '08:30',
      kindergartenPublicId: selectedKindergarten?.publicId || child.kindergartenPublicId,
      kindergartenName: selectedKindergarten?.name || child.kindergartenName,
      kindergartenAddress: selectedKindergarten
        ? `${selectedKindergarten.street} ${selectedKindergarten.houseNumber || ''}, ${selectedKindergarten.city}`
        : child.kindergartenAddress,
      sundayArrivalTime: arrivalTimes.sunday,
      mondayArrivalTime: arrivalTimes.monday,
      tuesdayArrivalTime: arrivalTimes.tuesday,
      wednesdayArrivalTime: arrivalTimes.wednesday,
      thursdayArrivalTime: arrivalTimes.thursday,
      fridayArrivalTime: arrivalTimes.friday,
    };

    onUpdate(updatedChild);
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 14px',
    fontSize: '15px',
    border: `2px solid ${colors.border}`,
    borderRadius: '10px',
    outline: 'none',
    boxSizing: 'border-box' as const,
    direction: 'rtl' as const,
  };

  const labelStyle = {
    fontSize: '13px',
    fontWeight: '500' as const,
    color: colors.text,
    marginBottom: '6px',
    display: 'block',
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onMouseDown={(e) => {
        // Only close if clicking directly on backdrop, not when dragging from inside
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '24px 24px 0 0',
          padding: '20px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        {/* Hidden file inputs */}
        <input type="file" ref={fileInputRef} onChange={handleImageSelect} accept="image/*" style={{ display: 'none' }} />
        <input type="file" ref={cameraInputRef} onChange={handleImageSelect} accept="image/*" capture="environment" style={{ display: 'none' }} />

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text }}>
            עריכת {child.firstName}
          </h2>
          <motion.button
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: colors.grayLight,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            whileTap={{ scale: 0.9 }}
          >
            <X size={18} color={colors.gray} />
          </motion.button>
        </div>

        {/* Photo */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ margin: '0 auto 12px', width: 'fit-content' }}>
            <ChildAvatar
              photoUrl={photoUrl}
              firstName={firstName || 'ילד'}
              lastName={lastName}
              size={100}
              borderRadius={20}
              borderWidth={3}
              borderColor={colors.primary}
            />
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <motion.button
              type="button"
              onClick={() => cameraInputRef.current?.click()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                backgroundColor: colors.primaryLight,
                color: colors.primary,
                border: 'none',
                borderRadius: '10px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
              whileTap={{ scale: 0.95 }}
            >
              <Camera size={14} />
              צלם
            </motion.button>

            <motion.button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                backgroundColor: colors.secondaryLight,
                color: colors.secondary,
                border: 'none',
                borderRadius: '10px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
              whileTap={{ scale: 0.95 }}
            >
              <ImageIcon size={14} />
              גלריה
            </motion.button>

            {photoUrl && (
              <motion.button
                type="button"
                onClick={() => setPhotoUrl(undefined)}
                style={{
                  padding: '8px 14px',
                  backgroundColor: '#fee2e2',
                  color: '#dc2626',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
                whileTap={{ scale: 0.95 }}
              >
                הסר
              </motion.button>
            )}
          </div>
        </div>

        {/* Form Fields */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Name fields */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>שם פרטי *</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>שם משפחה *</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                style={inputStyle}
              />
            </div>
          </div>

          {/* Kindergarten */}
          <div>
            <label style={labelStyle}>גן</label>
            <motion.button
              type="button"
              onClick={() => setShowKindergartenSearch(!showKindergartenSearch)}
              style={{
                ...inputStyle,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                backgroundColor: 'white',
              }}
            >
              <span>{selectedKindergarten?.name || child.kindergartenName}</span>
              <Building2 size={16} color={colors.textLight} />
            </motion.button>

            {showKindergartenSearch && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                style={{
                  marginTop: '8px',
                  padding: '12px',
                  backgroundColor: colors.grayLight,
                  borderRadius: '12px',
                }}
              >
                <input
                  type="text"
                  value={searchKindergarten}
                  onChange={(e) => setSearchKindergarten(e.target.value)}
                  placeholder="חפש גן..."
                  style={{ ...inputStyle, marginBottom: '10px' }}
                />
                <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                  {filteredKindergartens.map(k => (
                    <motion.div
                      key={k.publicId}
                      onClick={() => {
                        setSelectedKindergarten(k);
                        setShowKindergartenSearch(false);
                      }}
                      style={{
                        padding: '10px',
                        borderRadius: '8px',
                        marginBottom: '6px',
                        backgroundColor: selectedKindergarten?.publicId === k.publicId ? colors.secondaryLight : 'white',
                        border: `1px solid ${selectedKindergarten?.publicId === k.publicId ? colors.secondary : colors.border}`,
                        cursor: 'pointer',
                      }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <p style={{ fontWeight: '500', fontSize: '14px', marginBottom: '2px' }}>{k.name}</p>
                      <p style={{ fontSize: '12px', color: colors.textLight }}>
                        {k.street} {k.houseNumber}, {k.city}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>

          {/* Arrival Times */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <label style={labelStyle}>שעת הגעה אחרונה</label>

            {/* Kindergarten max arrival time hint */}
            {effectiveKindergarten?.operatingDays && effectiveKindergarten.operatingDays.length > 0 && (() => {
              const maxTime = getEarliestMaxArrivalTime(effectiveKindergarten, arrivalTimes);
              return maxTime ? (
                <div style={{ padding: '8px 12px', borderRadius: '8px', backgroundColor: '#fef3c7', border: '1px solid #f59e0b' }}>
                  <p style={{ fontSize: '12px', color: '#92400e', textAlign: 'center' }}>
                    ⏰ שעת הגעה אחרונה לגן: <strong>{maxTime}</strong>
                  </p>
                </div>
              ) : null;
            })()}

            {/* Toggle: All days vs individual */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <motion.button
                type="button"
                onClick={() => setUseAllDaysTime(true)}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '10px',
                  border: `2px solid ${useAllDaysTime ? colors.secondary : colors.border}`,
                  backgroundColor: useAllDaysTime ? colors.secondaryLight : 'white',
                  color: useAllDaysTime ? colors.secondary : colors.text,
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
                whileTap={{ scale: 0.98 }}
              >
                שעה אחת לכל הימים
              </motion.button>
              <motion.button
                type="button"
                onClick={() => setUseAllDaysTime(false)}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '10px',
                  border: `2px solid ${!useAllDaysTime ? colors.secondary : colors.border}`,
                  backgroundColor: !useAllDaysTime ? colors.secondaryLight : 'white',
                  color: !useAllDaysTime ? colors.secondary : colors.text,
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
                whileTap={{ scale: 0.98 }}
              >
                שעה שונה לכל יום
              </motion.button>
            </div>

            {/* Uniform time mode */}
            {useAllDaysTime && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Days selection: א-ה or א-ו */}
                <div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <motion.button
                      type="button"
                      onClick={() => {
                        setIncludeFriday(false);
                        const newTimes: Record<string, string | null> = {};
                        daysOfWeek.forEach(day => {
                          newTimes[day.key] = day.key === 'friday' ? null : allDaysTime;
                        });
                        setArrivalTimes(newTimes);
                      }}
                      style={{
                        flex: 1,
                        padding: '10px',
                        borderRadius: '10px',
                        border: `2px solid ${!includeFriday ? colors.primary : colors.border}`,
                        backgroundColor: !includeFriday ? colors.primaryLight : 'white',
                        color: !includeFriday ? colors.primary : colors.text,
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                      }}
                      whileTap={{ scale: 0.98 }}
                    >
                      א' - ה'
                    </motion.button>
                    <motion.button
                      type="button"
                      onClick={() => {
                        setIncludeFriday(true);
                        const newTimes: Record<string, string | null> = {};
                        daysOfWeek.forEach(day => {
                          newTimes[day.key] = allDaysTime;
                        });
                        setArrivalTimes(newTimes);
                      }}
                      style={{
                        flex: 1,
                        padding: '10px',
                        borderRadius: '10px',
                        border: `2px solid ${includeFriday ? colors.primary : colors.border}`,
                        backgroundColor: includeFriday ? colors.primaryLight : 'white',
                        color: includeFriday ? colors.primary : colors.text,
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                      }}
                      whileTap={{ scale: 0.98 }}
                    >
                      א' - ו'
                    </motion.button>
                  </div>
                </div>

                {/* Time picker */}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center', direction: 'ltr' }}>
                  <select
                    value={allDaysTime.split(':')[0] || '08'}
                    onChange={(e) => {
                      const minutes = allDaysTime.split(':')[1] || '00';
                      const newTime = `${e.target.value}:${minutes}`;
                      setAllDaysTime(newTime);
                      const newTimes: Record<string, string | null> = {};
                      daysOfWeek.forEach(day => {
                        newTimes[day.key] = day.key === 'friday' ? (includeFriday ? newTime : null) : newTime;
                      });
                      setArrivalTimes(newTimes);
                    }}
                    style={{
                      padding: '12px 18px',
                      fontSize: '18px',
                      fontWeight: '600',
                      border: `2px solid ${colors.border}`,
                      borderRadius: '10px',
                      backgroundColor: 'white',
                      textAlign: 'center',
                      width: '80px',
                    }}
                  >
                    {Array.from({ length: 13 }, (_, i) => i + 6).map(hour => (
                      <option key={hour} value={hour.toString().padStart(2, '0')}>
                        {hour.toString().padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                  <span style={{ fontSize: '20px', fontWeight: 'bold', color: colors.text }}>:</span>
                  <select
                    value={allDaysTime.split(':')[1] || '00'}
                    onChange={(e) => {
                      const hours = allDaysTime.split(':')[0] || '08';
                      const newTime = `${hours}:${e.target.value}`;
                      setAllDaysTime(newTime);
                      const newTimes: Record<string, string | null> = {};
                      daysOfWeek.forEach(day => {
                        newTimes[day.key] = day.key === 'friday' ? (includeFriday ? newTime : null) : newTime;
                      });
                      setArrivalTimes(newTimes);
                    }}
                    style={{
                      padding: '12px 18px',
                      fontSize: '18px',
                      fontWeight: '600',
                      border: `2px solid ${colors.border}`,
                      borderRadius: '10px',
                      backgroundColor: 'white',
                      textAlign: 'center',
                      width: '80px',
                    }}
                  >
                    {['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map(minute => (
                      <option key={minute} value={minute}>
                        {minute}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Summary */}
                <div style={{ padding: '10px', borderRadius: '10px', backgroundColor: colors.grayLight }}>
                  <p style={{ fontSize: '12px', color: colors.textLight, textAlign: 'center' }}>
                    שעת הגעה: {allDaysTime} | ימים: {includeFriday ? "א'-ו'" : "א'-ה'"}
                  </p>
                </div>
              </div>
            )}

            {/* Per-day time mode */}
            {!useAllDaysTime && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {daysOfWeek.map(day => (
                  <div key={day.key} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ width: '50px', fontSize: '13px', fontWeight: '500', color: colors.text }}>
                      {day.fullLabel}
                    </span>
                    <div style={{ display: 'flex', gap: '4px', flex: 1, direction: 'ltr' }}>
                      <select
                        value={arrivalTimes[day.key]?.split(':')[0] || '08'}
                        onChange={(e) => {
                          const currentTime = arrivalTimes[day.key] || '08:00';
                          const minutes = currentTime.split(':')[1] || '00';
                          setArrivalTimes({ ...arrivalTimes, [day.key]: `${e.target.value}:${minutes}` });
                        }}
                        disabled={!arrivalTimes[day.key]}
                        style={{
                          padding: '8px',
                          fontSize: '15px',
                          fontWeight: '600',
                          border: `2px solid ${colors.border}`,
                          borderRadius: '8px',
                          backgroundColor: arrivalTimes[day.key] ? 'white' : colors.grayLight,
                          flex: 1,
                          textAlign: 'center',
                        }}
                      >
                        {Array.from({ length: 13 }, (_, i) => i + 6).map(hour => (
                          <option key={hour} value={hour.toString().padStart(2, '0')}>
                            {hour.toString().padStart(2, '0')}
                          </option>
                        ))}
                      </select>
                      <span style={{ alignSelf: 'center', fontWeight: 'bold' }}>:</span>
                      <select
                        value={arrivalTimes[day.key]?.split(':')[1] || '00'}
                        onChange={(e) => {
                          const currentTime = arrivalTimes[day.key] || '08:00';
                          const hours = currentTime.split(':')[0] || '08';
                          setArrivalTimes({ ...arrivalTimes, [day.key]: `${hours}:${e.target.value}` });
                        }}
                        disabled={!arrivalTimes[day.key]}
                        style={{
                          padding: '8px',
                          fontSize: '15px',
                          fontWeight: '600',
                          border: `2px solid ${colors.border}`,
                          borderRadius: '8px',
                          backgroundColor: arrivalTimes[day.key] ? 'white' : colors.grayLight,
                          flex: 1,
                          textAlign: 'center',
                        }}
                      >
                        {['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map(minute => (
                          <option key={minute} value={minute}>
                            {minute}
                          </option>
                        ))}
                      </select>
                    </div>
                    <motion.button
                      type="button"
                      onClick={() => setArrivalTimes({ ...arrivalTimes, [day.key]: arrivalTimes[day.key] ? null : '08:30' })}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        border: 'none',
                        backgroundColor: arrivalTimes[day.key] ? colors.successLight : colors.grayLight,
                        color: arrivalTimes[day.key] ? colors.success : colors.textLight,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {arrivalTimes[day.key] ? <CheckCircle2 size={16} /> : <X size={16} />}
                    </motion.button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '24px' }}>
          <motion.button
            type="button"
            onClick={() => onDelete(child.id)}
            style={{
              padding: '14px',
              borderRadius: '12px',
              backgroundColor: '#fee2e2',
              color: '#dc2626',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            whileTap={{ scale: 0.98 }}
          >
            <Trash2 size={20} />
          </motion.button>

          <motion.button
            type="button"
            onClick={onClose}
            style={{
              flex: 1,
              padding: '14px',
              borderRadius: '12px',
              backgroundColor: colors.grayLight,
              color: colors.text,
              fontSize: '15px',
              fontWeight: '600',
              border: 'none',
              cursor: 'pointer',
            }}
            whileTap={{ scale: 0.98 }}
          >
            ביטול
          </motion.button>

          <motion.button
            type="button"
            onClick={handleSubmit}
            style={{
              flex: 1,
              padding: '14px',
              borderRadius: '12px',
              background: `linear-gradient(135deg, ${colors.secondary} 0%, #0d9488 100%)`,
              color: 'white',
              fontSize: '15px',
              fontWeight: '600',
              border: 'none',
              cursor: 'pointer',
            }}
            whileTap={{ scale: 0.98 }}
          >
            שמור
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}
